import axios from 'axios';
// import queryString from 'query-string';
import { Modal } from 'antd';
import { hasIn, isString } from 'lodash-es';
import React from 'react';
import DOMPurify from 'dompurify';
import {Monitor} from '@/monitor';
import { BusinessError } from '@/errors';
// import globalStore from '@/stores';
// import { getIntl } from './intl';

// const isAbsolutePathReg = /^https?:/;

function fileDownload(data: Blob, filename: string, mime?: string) {
  const blobData = [data];
  const blob = new Blob(blobData, { type: mime || 'application/octet-stream' });
  const blobURL = (window.URL && window.URL.createObjectURL) ? window.URL.createObjectURL(blob) : window.webkitURL.createObjectURL(blob);
  const tempLink = document.createElement('a');
  tempLink.style.display = 'none';
  tempLink.href = blobURL;
  tempLink.setAttribute('download', filename);

  // Safari thinks _blank anchor are pop ups. We only want to set _blank
  // target if the browser does not support the HTML5 download attribute.
  // This allows you to download files in desktop safari if pop up blocking
  // is enabled.
  if (typeof tempLink.download === 'undefined') {
    tempLink.setAttribute('target', '_blank');
  }

  document.body.appendChild(tempLink);
  tempLink.click();

  // Fixes "webkit blob resource error 1"
  setTimeout(function () {
    document.body.removeChild(tempLink);
    window.URL.revokeObjectURL(blobURL);
  }, 200);
}

export type CustomizeConfigType = {
  /**
   * 是否抛出接口标准错误
   *
   * 响应标准格式为：
   * { data: any, info: string, status: 0 | 1 }
   *
   * 设为 true 时，当 status = 0 则弹出错误
   *
   * 注意：此选项设为 false 可以关闭该行为，但需要自行处理错误
   */
  throwError?: boolean;
  /**
   * 请求缓存
   *
   * - route 路由级别的缓存
   * - global 页面全局缓存
   * - false 不缓存
   */
  cache?: 'route' | 'global' | false;
  /**
   * 获取请求的abort方法
   */
  getAbort?: (abort: Function) => void;
  /**
  * 是否为文件下载
  */
  download?: boolean;
  /**
   * 自定义请求头
   */
  headers?: Record<string, string>;
};

const instance = axios.create({
  baseURL: window.__MICRO_APP_ENVIRONMENT__
    ? window.microAppConfig.apiUrl
    : '/api',
  headers: {
    'X-Requested-With': 'XMLHttpRequest'
  }
});

instance.interceptors.request.use((config: any) => {
  const customizeConfig: CustomizeConfigType = config.__customizeConfig || {};

  if (customizeConfig.headers) {
    config.headers = {
      ...config.headers,
      ...customizeConfig.headers
    };
  }

  if (customizeConfig.download) {
    config.responseType = 'blob';
  }

  let _params = config.data || config.params;
  if (_params instanceof FormData) {
    _params = Object.fromEntries(_params);
  }
  config._params = _params;
  config._startTime = new Date().getTime();
  config._href = window.location.href;
  performance.mark('apiStart');
  return config;
});

instance.interceptors.response.use((response: any) => {
  // globalStore.hideLoading();
  // const intl = getIntl();

  performance.mark('apiEnd');
  Monitor.integrations.ApiRequestListener.measure(response);

  const customizeConfig: CustomizeConfigType = response.config.__customizeConfig;
  if (response.status === 200) {
    const data = response.data;
    if (response.headers['content-disposition']) {
      const filename = response.headers['content-disposition']?.split('filename=')?.[1]?.replace?.(/['"]/g, '');
      if (filename) {
        fileDownload(data, decodeURIComponent(filename));
        return Promise.resolve(response.data);
      }
    } else if (data instanceof Blob && data.type === 'application/json') {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.addEventListener('loadend', function () { //
          const res = JSON.parse(reader.result as string);
          if (res.status === 0 && res.info) {
            Modal.error({
              title: res.info
            });
            return reject(res.info);
          } else {
            return resolve(res);
          }
        });
        reader.readAsText(data, 'utf-8');
      });
    }
    // 是否为分页数据
    const isPaginationResults = hasIn(data, 'aoData') &&
      hasIn(data, 'iTotalDisplayRecords') &&
      hasIn(data, 'iTotalRecords') &&
      hasIn(data, 'sEcho');

    if (data.status === 1 ||
      isPaginationResults ||
      (customizeConfig || {}).throwError === false) {
      return Promise.resolve(data);
    } else if (data.code === 401) {
      // new Promise((resolve: Function) => {
      //   if (globalStore.hasLogin) {
      //     globalStore.setToken('');

      //     Modal.warning({
      //       title: intl.formatMessage({
      //         id: 'common.LoginStatusInvalid',
      //         defaultMessage: '登录状态已失效，请重新登录！'
      //       }),
      //       // icon: <ExclamationCircleOutlined />,
      //       okText: intl.formatMessage({ id: 'common.ReLogin', defaultMessage: '重新登录' }),
      //       onOk() {
      //         resolve();
      //       },
      //     });
      //   } else {
      //     resolve();
      //   }
      // }).then(() => {
      //   const { url } = queryString.parseUrl(window.location.href);
      //   window.location.href = `${data.data.callbackUrl}?service=${encodeURIComponent(`${url}?showjwt=jwt`)}#/login`;
      // });

      // return;
      return;
    } else {
      if (!window.__MICRO_APP_ENVIRONMENT__) {
        if (data?.errorCode == 'LOGIN_TIMEOUT') { //登录超时
          window.location.href = '/User/login';
          return;
        }
        if (data?.errorCode == 'UNSIGNED_AGREEMENT') { //未签署新主体供应链管理协议的已开通服务的客户 需求：6262738810
          window.location.href = '/AgreementSign/index';
          return;
        }
      }
      if (data.info) {
        Modal.error({
          title: data.info,
          className: 'whitespace-pre-line',
          // 报错提示显示在 popconfirm(z-index:1060) 上面
          zIndex: 1070,
        });
      }
      return Promise.reject(new BusinessError(data.info));
    }
  }

  return Promise.reject(response);
}, error => {

  performance.mark('apiEnd');
  Monitor.integrations.ApiRequestListener.measure(error);

  if (isString(error)) {
    Modal.error({
      title: error,
      className: 'whitespace-pre-line'
    });
  } else if (isString(error?.response?.data)) {
    // WAF 拦截错误提示
    Modal.error({
      content: React.createElement(
        'div',
        {
          dangerouslySetInnerHTML: { __html: DOMPurify.sanitize(error?.response?.data,{FORBID_TAGS: ['style']}) },
        },
      )
    });
  }
  return Promise.reject(error);
});

const request = (options = {}) => {
  return instance.request(options);
};

const get = (url: string, params = {}, options = {}) => {
  return instance.request({
    ...options,
    url,
    params,
  });
};

const post = (url: string, data = {}, options = {}) => {
  return instance.request({
    method: 'post',
    ...options,
    url,
    data,
  });
};

/* const upload = (url, data = {}, options = {}) => {
  return instance.request({
    ...options,
    url,
    data,
  });
} */

export {
  request,
  get,
  post,
};

export default request;
